import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea,
  Paper, Alert, Chip,
} from '@mui/material';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CampaignIcon from '@mui/icons-material/Campaign';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TableChartIcon from '@mui/icons-material/TableChart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import VerifiedIcon from '@mui/icons-material/Verified';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import StatCard from '../../components/common/StatCard';
import StatusBadge from '../../components/common/StatusBadge';
import DataTable from '../../components/common/DataTable';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const quickActions = [
  { label: 'Submit Application', icon: SendIcon, color: '#1B365D', description: 'Submit a new referral case', path: '/agent/submit' },
  { label: 'My Sales', icon: VisibilityIcon, color: '#00B894', description: 'View your submitted cases', path: '/agent/sales' },
  { label: 'Marketing Materials', icon: CampaignIcon, color: '#6C5CE7', description: 'Browse and share approved content', path: '/partner/marketing' },
  { label: 'Commission Table', icon: TableChartIcon, color: '#E17055', description: 'View commission rates', path: '/commission-table' },
];

const caseColumns = [
  { field: 'id', headerName: 'Case ID', width: 130 },
  { field: 'applicantName', headerName: 'Customer' },
  { field: 'productName', headerName: 'Product' },
  { field: 'bank', headerName: 'Bank' },
  {
    field: 'amount', headerName: 'Amount', align: 'right',
    renderCell: (row) => `RM ${Number(row.amount || 0).toLocaleString()}`,
  },
  {
    field: 'status', headerName: 'Status',
    renderCell: (row) => <StatusBadge status={row.status} />,
  },
  {
    field: 'createdAt', headerName: 'Created',
    renderCell: (row) => new Date(row.createdAt).toLocaleDateString(),
  },
];

export default function AgentPortal() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Agent's own cases (no downline)
  const myCases = useMemo(() => {
    if (!user?.id) return [];
    return db.cases.getByReferralOwnerId(user.id);
  }, [user]);

  const stats = useMemo(() => {
    const total = myCases.length;
    const approved = myCases.filter((c) => ['Approved', 'Disbursed'].includes(c.status)).length;
    const pending = myCases.filter((c) =>
      ['Submitted', 'In Review', 'Pending Document', 'Pending Bank Decision'].includes(c.status)
    ).length;
    const commission = myCases.reduce((sum, c) => sum + (Number(c.commissionAmount) || 0), 0);
    return { total, approved, pending, commission };
  }, [myCases]);

  const recentCases = useMemo(
    () => myCases
      .slice()
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 10),
    [myCases]
  );

  const kycVerified = user?.kycStatus === 'verified';

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome banner */}
      <Paper sx={{
        p: 3, mb: 3, borderRadius: 3,
        background: 'linear-gradient(135deg, #1B365D 0%, #2C5282 100%)',
        color: 'white',
      }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
              Welcome, {user?.name?.split(' ')[0] || 'Agent'}!
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Your referral code: <strong>{user?.referralCode || '—'}</strong>
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
            <Chip
              icon={kycVerified
                ? <VerifiedIcon sx={{ fontSize: 16, color: 'white !important' }} />
                : <GppMaybeIcon sx={{ fontSize: 16, color: 'white !important' }} />}
              label={kycVerified ? 'eKYC Verified' : 'eKYC Required'}
              sx={{
                backgroundColor: kycVerified ? 'rgba(76,175,80,0.3)' : 'rgba(255,152,0,0.3)',
                color: 'white',
                fontWeight: 700,
                border: '1px solid rgba(255,255,255,0.3)',
              }}
              onClick={() => navigate('/profile')}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* KYC warning */}
      {!kycVerified && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <strong>Your eKYC is not verified.</strong> Any commission you earn will be held pending until you complete identity verification.{' '}
          <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }}
            onClick={() => navigate('/profile')}>
            Verify now →
          </Box>
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard title="Total Cases" value={stats.total} icon={FolderSharedIcon} color={PRIMARY} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Approved" value={stats.approved} icon={CheckCircleOutlineIcon} color={SECONDARY} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard title="Pending" value={stats.pending} icon={HourglassEmptyIcon} color="#F57F17" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Total Commission"
            value={`RM ${stats.commission.toLocaleString()}`}
            icon={AccountBalanceWalletIcon}
            color="#7B1FA2"
          />
        </Grid>
      </Grid>

      {/* Quick actions */}
      <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 2 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {quickActions.map((action) => (
          <Grid item xs={12} sm={6} md={3} key={action.label}>
            <Card sx={{
              borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.12)' },
            }}>
              <CardActionArea
                sx={{ p: 2.5 }}
                onClick={() => navigate(action.path)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: 2,
                    backgroundColor: `${action.color}15`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <action.icon sx={{ color: action.color, fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY }}>
                      {action.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {action.description}
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent cases */}
      <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 2 }}>
        Recent Cases
      </Typography>
      {recentCases.length > 0 ? (
        <DataTable columns={caseColumns} rows={recentCases} title="" searchable={false} pageSize={10} />
      ) : (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          No cases yet. Click "Submit Application" to create your first case.
        </Alert>
      )}
    </Box>
  );
}
